<template>
  <div
    class="template-stage-card border-sm rounded-lg overflow-hidden"
    :class="{ 'border-primary': isEditing }"
  >
    <div class="bg-bgCanvas border-b-sm d-flex align-center ga-2 py-3 px-4">
      <v-icon
        icon="mdi:drag-vertical"
        size="16"
        color="textLight"
        class="drag-handle cursor-grab"
      />

      <v-avatar
        size="22"
        color="primary"
        class="text-caption text-white"
      >
        {{ stage.stage_number }}
      </v-avatar>

      <span
        v-if="!isEditing"
        class="text-body-2 text-textDarkest cursor-pointer flex-grow-1"
        @click="startEdit"
      >
        {{ stage.name }}
      </span>

      <DpplTextField
        v-else
        v-model="editName"
        v-click-outside="handleClickOutside"
        label=""
        hide-details
        autofocus
        container-class="flex-grow-1 template-stage-card__name-input"
        @keyup.enter="confirmEdit"
      />

      <HideRbac :permissions="['updateApprovalTemplate']">
        <v-btn
          variant="outlined"
          rounded="lg"
          height="32"
          width="32"
          icon
          color="strokeDefault"
          aria-label="Remove stage"
          @click="$emit('remove')"
        >
          <v-icon
            icon="$delete-outline"
            size="20"
            color="textDark"
          />
        </v-btn>
      </HideRbac>
    </div>

    <div class="pa-3">
      <p class="text-body-3 text-textLight mb-2">
        Slots in this stage run in parallel
      </p>

      <div class="d-flex flex-column ga-2">
        <TemplateSlotRow
          v-for="(slot, index) in stage.slots"
          :key="index"
          :item="slot"
          @update="(data) => handleSlotUpdate(index, data)"
          @remove="$emit('remove-slot', index)"
        />
      </div>

      <HideRbac :permissions="['updateApprovalTemplate']">
        <v-btn
          variant="text"
          class="mt-2 text-button-3 w-100 justify-start text-textLight border-sm border-dashed"
          @click="$emit('add-slot')"
        >
          <template #prepend>
            <v-icon
              icon="$plus"
              size="13"
            />
          </template>
          Add Slot
        </v-btn>
      </HideRbac>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useAppAuthData } from '@/stores/app/authData';
import HideRbac from '@/components/rbac/HideRbac.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import TemplateSlotRow from './TemplateSlotRow.vue';

defineOptions({ name: 'TemplateStageCard' });

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update', 'remove', 'add-slot', 'remove-slot']);

const { checkRbacPermissions } = useAppAuthData();
const isEditing = ref(false);
const editName = ref('');

const startEdit = () => {
  if (!checkRbacPermissions(['updateApprovalTemplate'])) return;
  if (isEditing.value) return;
  editName.value = props.stage.name;
  isEditing.value = true;
};

const confirmEdit = () => {
  emit('update', editName.value || props.stage.name);
  isEditing.value = false;
};

const handleClickOutside = () => {
  if (isEditing.value) confirmEdit();
};

const handleSlotUpdate = (index, data) => {
  Object.assign(props.stage.slots[index], data);
};
</script>

<style lang="less" scoped>
.template-stage-card {
  :deep(.v-input__control) {
    background-color: rgb(var(--v-theme-bgDefault));
  }
}
</style>
